import { motion, AnimatePresence } from 'framer-motion';
import { Mic, X, Send } from 'lucide-react';
import { formatTime } from '../hooks/useVoiceRecorder';

/**
 * Animated waveform visualization during recording
 */
export function AudioWaveform({ level = 0, isActive = false }) {
  const bars = 5;
  
  return (
    <div className="flex items-center justify-center gap-0.5 h-8">
      {[...Array(bars)].map((_, i) => {
        // Create varied heights based on position and audio level
        const baseHeight = 0.3 + (Math.sin(i * 0.8) * 0.2);
        const dynamicHeight = isActive ? baseHeight + (level * 0.5 * (1 + Math.sin(i + Date.now() / 100) * 0.3)) : 0.2;
        const height = Math.min(Math.max(dynamicHeight, 0.15), 1);
        
        return (
          <motion.div
            key={i}
            className="w-1 bg-sophia-pink-500 rounded-full"
            initial={{ height: 4 }}
            animate={{ 
              height: isActive ? height * 28 + 4 : 4,
              opacity: isActive ? 0.8 + (level * 0.2) : 0.4
            }}
            transition={{ 
              duration: 0.1,
              ease: "easeOut"
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Pulsing recording indicator
 */
export function RecordingPulse({ isRecording }) {
  return (
    <AnimatePresence>
      {isRecording && (
        <>
          {/* Outer pulse ring */}
          <motion.div
            className="absolute inset-0 rounded-full bg-sophia-pink-500/30"
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{ 
              scale: [1, 1.5, 1.8],
              opacity: [0.5, 0.2, 0]
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
          {/* Inner pulse ring */}
          <motion.div
            className="absolute inset-0 rounded-full bg-sophia-pink-500/40"
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ 
              scale: [1, 1.3, 1.5],
              opacity: [0.6, 0.3, 0]
            }}
            transition={{ 
              duration: 1.2,
              repeat: Infinity,
              ease: "easeOut",
              delay: 0.2
            }}
          />
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Voice recorder button with all recording UI
 */
export function VoiceRecorderButton({
  isRecording,
  audioLevel,
  recordingTime,
  transcript,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  disabled,
  language = 'en'
}) {
  const t = (key) => {
    const translations = {
      'listening': language === 'de' ? 'Ich höre zu...' : 'Listening...',
      'tapToSpeak': language === 'de' ? 'Tippen zum Sprechen' : 'Tap to speak',
      'cancel': language === 'de' ? 'Abbrechen' : 'Cancel',
      'send': language === 'de' ? 'Senden' : 'Send',
    };
    return translations[key] || key;
  };

  if (isRecording) {
    return (
      <motion.div
        className="flex items-center gap-2 bg-sophia-pink-500/10 rounded-full px-3 py-2 border border-sophia-pink-500/30"
        initial={{ opacity: 0, width: 48 }}
        animate={{ opacity: 1, width: 'auto' }}
        exit={{ opacity: 0, width: 48 }}
      >
        {/* Cancel button */}
        <motion.button
          data-testid="voice-cancel-btn"
          onClick={onCancelRecording}
          className="w-10 h-10 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500/30 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <X className="w-5 h-5" />
        </motion.button>

        {/* Waveform */}
        <div className="flex-1 min-w-[80px]">
          <AudioWaveform level={audioLevel} isActive={true} />
        </div>

        {/* Recording time */}
        <span className="text-sophia-pink-500 font-mono text-sm min-w-[40px] text-center">
          {formatTime(recordingTime)}
        </span>

        {/* Send/Stop button */}
        <motion.button
          data-testid="voice-send-btn"
          onClick={onStopRecording}
          className="w-10 h-10 rounded-full bg-sophia-pink-500 text-white flex items-center justify-center hover:bg-sophia-pink-600 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Send className="w-5 h-5" />
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.button
      data-testid="voice-record-btn"
      onClick={onStartRecording}
      disabled={disabled}
      className="relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 bg-card border border-border text-muted-foreground hover:text-foreground hover:border-sophia-pink-500/50 disabled:opacity-40"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.9 }}
    >
      <Mic className="w-5 h-5" />
    </motion.button>
  );
}

/**
 * Voice message bubble for displaying recorded voice messages
 */
export function VoiceMessageBubble({ 
  text, 
  isUser = true, 
  isVoiceMessage = false,
  language = 'en' 
}) {
  const t = (key) => {
    const translations = {
      'voiceMessage': language === 'de' ? 'Sprachnachricht' : 'Voice message',
    };
    return translations[key] || key;
  };

  return (
    <div
      className={
        isUser
          ? 'bg-sophia-pink-500/15 text-foreground rounded-2xl rounded-tr-sm p-4 border border-sophia-pink-500/20'
          : 'bg-card text-foreground rounded-2xl rounded-tl-sm p-4 border border-border/50 shadow-sm'
      }
    >
      {isVoiceMessage && (
        <div className="flex items-center gap-1.5 mb-1.5 text-sophia-pink-500">
          <Mic className="w-3.5 h-3.5" />
          <span className="text-[10px] font-medium uppercase tracking-wide">{t('voiceMessage')}</span>
        </div>
      )}
      <p className="font-body text-sm sm:text-base leading-relaxed">{text}</p>
    </div>
  );
}
